// ============================================
// Manor Miser - Game Scene (native resolution)
// ============================================

import Phaser from 'phaser';
import { socketManager } from '../network/SocketManager';
import { SoundManager } from '../audio/SoundManager';
import type { PlayerPublic, BetResult, EliminationResult } from '../shared/types';
import { BETTING_DURATION } from '../shared/constants';
import {
  TILE_SIZE, BOARD_SIZE,
  PLAYER_TILE_POSITIONS, tileToScreen,
  getOrientation, getLayout,
  type HUDLayout,
} from '../layout/LayoutConstants';
import { generateRoom, type RoomLayout } from '../map/RoomGenerator';
import { findPath, simplifyPath, type TileCoord } from '../map/Pathfinder';

type GamePhase = 'betting' | 'reveal' | 'elimination' | 'gameOver';

interface PlayerVisual {
  sprite: Phaser.GameObjects.Sprite;
  coinThrowX: number;
  coinThrowY: number;
  nameLabel: Phaser.GameObjects.BitmapText;
  betBubble: Phaser.GameObjects.Container;
  idleTimer?: Phaser.Time.TimerEvent;
}

interface SimpleBetButton {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.BitmapText;
  amount: 1 | 5 | 10;
  disabled: boolean;
}

export class GameScene extends Phaser.Scene {
  private phase: GamePhase = 'betting';
  private roundNumber: number = 1;
  private myMoney: number = 100;
  private myBet: number = 0;
  private alivePlayers: PlayerPublic[] = [];
  private playerId: string = '';
  private amIAlive: boolean = true;

  // Layout (resolved per orientation in create())
  private hud!: HUDLayout;

  // Room layout (generated dynamically)
  private roomLayout!: RoomLayout;

  // King NPC
  private kingSprite?: Phaser.GameObjects.Sprite;
  private kingArrived: boolean = false;
  private kingPacingTween?: Phaser.Tweens.Tween;
  private kingAtPodium: boolean = false;

  // Visuals
  private playerVisuals: Map<string, PlayerVisual> = new Map();
  private timerText!: Phaser.GameObjects.BitmapText;
  private roundText!: Phaser.GameObjects.BitmapText;
  private moneyText!: Phaser.GameObjects.BitmapText;
  private betText!: Phaser.GameObjects.BitmapText;
  private phaseText!: Phaser.GameObjects.BitmapText;
  private messageText!: Phaser.GameObjects.BitmapText;
  private spectatorText!: Phaser.GameObjects.BitmapText;
  private betButtons: SimpleBetButton[] = [];

  // Timer
  private timerEvent: Phaser.Time.TimerEvent | null = null;
  private phaseEndsAt: number = 0;
  private progressBarBg!: Phaser.GameObjects.Graphics;
  private progressBarFill!: Phaser.GameObjects.Graphics;

  // All players (including eliminated) for tracking
  private allPlayers: PlayerPublic[] = [];
  private playerSlots: Map<string, number> = new Map();

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data?: { orientationRestart?: boolean }): void {
    // On orientation restart, preserve game state — only clear visuals
    this.playerVisuals.clear();
    this.betButtons = [];
    this.stopTimer();

    if (!data?.orientationRestart) {
      // Fresh game start — reset all game state
      this.playerSlots.clear();
      this.allPlayers = [];
      this.alivePlayers = [];
      this.phase = 'betting';
      this.myBet = 0;
      this.amIAlive = true;
      this.kingArrived = false;
      this.kingAtPodium = false;
      this.roomLayout = generateRoom();
    }

    // Must be set before socket callbacks register: onRoundStarted can fire
    // before create() runs, and it uses playerId to compute amIAlive.
    this.playerId = this.registry.get('playerId') || '';

    this.setupSocketCallbacks();
  }

  create(): void {
    // Resolve layout for current orientation
    this.hud = getLayout(getOrientation());

    this.playerId = this.registry.get('playerId') || '';

    // Only load initial state if we don't already have players (fresh game, not orientation restart)
    if (this.allPlayers.length === 0) {
      this.myMoney = this.registry.get('yourMoney') || 100;
      const gameState = this.registry.get('gameState');
      if (gameState) {
        this.allPlayers = gameState.players;
        this.alivePlayers = gameState.players.filter((p: PlayerPublic) => p.isAlive);
        this.roundNumber = gameState.roundNumber || 1;
      } else {
        const players = this.registry.get('players') as PlayerPublic[] | null | undefined;
        if (players && players.length > 0) {
          this.allPlayers = players;
          this.alivePlayers = players.filter(p => p.isAlive);
        }
      }
    }

    this.createBackground();
    this.createHUD();
    this.createPlayerSprites();

    // Message text (center of board)
    this.messageText = this.add.bitmapText(
      BOARD_SIZE / 2, BOARD_SIZE / 2 + 14, 'pixel', ''
    );
    this.messageText.setOrigin(0.5).setScale(1).setTint(0xd4a855).setDepth(10);

    // King NPC — create or restore position
    this.createKing();

    this.cameras.main.fadeIn(300, 26, 20, 18);

    // Disable betting until king arrives and timer starts
    this.setBetButtonsEnabled(false);

    // On round 1 (first time), king walks to podium then timer starts.
    // On subsequent rounds or orientation restart, timer starts immediately.
    if (!this.kingArrived) {
      this.playKingEntrance(() => {
        this.kingArrived = true;
        this.initializeDefaultTimer();
        this.setBetButtonsEnabled(this.amIAlive);
      });
    } else {
      this.initializeDefaultTimer();
      this.setBetButtonsEnabled(this.amIAlive);
    }
  }

  // ========================================
  // Background
  // ========================================

  private createBackground(): void {
    const { width, height } = this.cameras.main;

    const bg = this.add.graphics();
    bg.fillStyle(0x1a1412, 1);
    bg.fillRect(0, 0, width, height);

    // Build tilemap from generated room layout — two layers for animation frames
    const layer0 = this.buildTilemapLayer('tiles-frame0', 'room-layer0');
    const layer1 = this.buildTilemapLayer('tiles-frame1', 'room-layer1');

    if (layer0) layer0.setTint(0xd4a855);
    if (layer1) {
      layer1.setTint(0xd4a855);
      layer1.setVisible(false);
    }

    // Slow animation: swap frames every 800ms
    if (layer0 && layer1) {
      this.time.addEvent({
        delay: 800,
        callback: () => {
          layer0.setVisible(!layer0.visible);
          layer1.setVisible(!layer1.visible);
        },
        loop: true,
      });
    }
  }

  /** Create a tilemap layer from the generated room data using the given tileset texture key */
  private buildTilemapLayer(textureKey: string, layerName: string): Phaser.Tilemaps.TilemapLayer | null {
    const map = this.make.tilemap({ tileWidth: TILE_SIZE, tileHeight: TILE_SIZE, width: 12, height: 12 });
    // gid=1 so tile IDs match Tiled's 1-based convention used in TileCatalog
    const tileset = map.addTilesetImage(textureKey, textureKey, TILE_SIZE, TILE_SIZE, 0, 0, 1);
    if (!tileset) return null;

    const layer = map.createBlankLayer(layerName, tileset, 0, 0, 12, 12, TILE_SIZE, TILE_SIZE);
    if (!layer) return null;

    const tiles = this.roomLayout.tiles;
    const rotations = this.roomLayout.tileRotations;
    for (let r = 0; r < 12; r++) {
      for (let c = 0; c < 12; c++) {
        if (tiles[r][c] > 0) {
          const tile = layer.putTileAt(tiles[r][c], c, r);
          if (rotations[r][c] !== 0) {
            tile.rotation = rotations[r][c];
          }
        }
      }
    }

    return layer;
  }

  // ========================================
  // King NPC (manor lord)
  // ========================================

  // King is drawn 3px higher so he looks grounded on the tiles
  private static readonly KING_Y_OFFSET = -3;

  private get kingDoor(): TileCoord { return this.roomLayout.door; }
  private get kingPodium(): TileCoord { return this.roomLayout.podium; }

  private createKing(): void {
    const yOff = GameScene.KING_Y_OFFSET;
    const podiumPx = tileToScreen(this.kingPodium.col, this.kingPodium.row);

    if (this.kingArrived) {
      this.kingSprite = this.add.sprite(podiumPx.x, podiumPx.y + yOff, 'king');
      this.kingSprite.setDepth(7).setTint(0xf5e6d3);
      this.kingSprite.play('king-idle_south');
      this.kingAtPodium = true;
    } else {
      const doorPx = tileToScreen(this.kingDoor.col, this.kingDoor.row);
      this.kingSprite = this.add.sprite(doorPx.x, doorPx.y - TILE_SIZE + yOff, 'king');
      this.kingSprite.setDepth(7).setTint(0xf5e6d3);
      this.kingSprite.play('king-idle_south');
    }
  }

  /** Walk king from door to podium using pathfinder, then call onComplete */
  private playKingEntrance(onComplete: () => void): void {
    if (!this.kingSprite) { onComplete(); return; }

    // Find path from the tile just inside the door to the podium
    const start: TileCoord = { col: this.kingDoor.col, row: this.kingDoor.row + 1 };
    // Walk to one tile north of podium (so king stands in front of it)
    const end: TileCoord = { col: this.kingPodium.col, row: this.kingPodium.row - 1 };
    const path = findPath(this.roomLayout.walkable, start, end);
    const waypoints = simplifyPath(path.length > 0 ? path : [start, end]);

    const king = this.kingSprite;
    const yOff = GameScene.KING_Y_OFFSET;
    const entryPx = tileToScreen(start.col, start.row);

    // First: walk south from above the door into the room
    king.play('king-walk_south');
    this.tweens.add({
      targets: king,
      x: entryPx.x,
      y: entryPx.y + yOff,
      duration: 600,
      ease: 'Linear',
      onComplete: () => {
        // Then follow the pathfinder waypoints
        this.moveKingAlongPath(waypoints, 40, () => {
          king.play('king-idle_south');
          this.kingAtPodium = true;
          this.time.delayedCall(2000, onComplete);
        });
      },
    });
  }

  /** Move king along a series of tile waypoints, playing correct walk animations */
  private moveKingAlongPath(waypoints: TileCoord[], speed: number, onComplete: () => void): void {
    const king = this.kingSprite;
    const yOff = GameScene.KING_Y_OFFSET;
    if (!king || waypoints.length < 2) { onComplete(); return; }

    let i = 1;

    const walkNext = () => {
      if (i >= waypoints.length) { onComplete(); return; }

      const from = waypoints[i - 1];
      const to = waypoints[i];
      const toPx = tileToScreen(to.col, to.row);

      const dr = to.row - from.row;
      const dc = to.col - from.col;
      let dir = 'south';
      if (dr < 0) dir = 'north';
      else if (dr > 0) dir = 'south';
      else if (dc < 0) dir = 'west';
      else if (dc > 0) dir = 'east';

      king.play(`king-walk_${dir}`);

      const dist = Math.abs(toPx.x - king.x) + Math.abs((toPx.y + yOff) - king.y);
      const duration = (dist / speed) * 1000;

      this.tweens.add({
        targets: king,
        x: toPx.x,
        y: toPx.y + yOff,
        duration: Math.max(100, duration),
        ease: 'Linear',
        onComplete: () => { i++; walkNext(); },
      });
    };

    walkNext();
  }

  /** Start king pacing left-right north of the podium during betting */
  private startKingPacing(): void {
    this.stopKingPacing();
    if (!this.kingSprite || !this.kingAtPodium) return;

    const paceRow = this.kingPodium.row - 1;
    const leftCol = Math.max(1, this.kingPodium.col - 2);
    const rightCol = Math.min(10, this.kingPodium.col + 2);
    const leftPx = tileToScreen(leftCol, paceRow);
    const rightPx = tileToScreen(rightCol, paceRow);
    const king = this.kingSprite;

    const paceRight = () => {
      if (!king.active) return;
      king.play('king-walk_east');
      this.kingPacingTween = this.tweens.add({
        targets: king,
        x: rightPx.x,
        duration: 2000,
        ease: 'Linear',
        onComplete: () => paceLeft(),
      });
    };

    const paceLeft = () => {
      if (!king.active) return;
      king.play('king-walk_west');
      this.kingPacingTween = this.tweens.add({
        targets: king,
        x: leftPx.x,
        duration: 2000,
        ease: 'Linear',
        onComplete: () => paceRight(),
      });
    };

    // Start pacing from current position
    paceRight();
  }

  private stopKingPacing(): void {
    if (this.kingPacingTween) {
      this.kingPacingTween.stop();
      this.kingPacingTween = undefined;
    }
  }

  /** King walks to the eliminated player, attacks, then returns to podium */
  private playKingAttack(eliminatedPlayerId: string, onComplete: () => void): void {
    const vis = this.playerVisuals.get(eliminatedPlayerId);
    const king = this.kingSprite;
    if (!vis || !king) { onComplete(); return; }

    this.stopKingPacing();
    this.kingAtPodium = false;

    const playerSlot = this.playerSlots.get(eliminatedPlayerId);
    const isFrontRow = playerSlot !== undefined && playerSlot < 4;

    // Determine attack position and animation direction
    const playerPos = PLAYER_TILE_POSITIONS[playerSlot ?? 0];
    let attackTile: TileCoord;
    let attackAnim: string;

    if (isFrontRow) {
      // Approach from north (one tile above), attack south
      attackTile = { col: playerPos.col, row: playerPos.row - 1 };
      attackAnim = 'king-attack_south';
    } else {
      // Approach from east (one tile right), attack west
      attackTile = { col: Math.min(10, playerPos.col + 1), row: playerPos.row };
      attackAnim = 'king-attack_west';
    }

    const attackPx = tileToScreen(attackTile.col, attackTile.row);

    // Find path from current king position to attack position
    const kingTile = this.pixelToTile(king.x, king.y);
    const path = findPath(this.roomLayout.walkable, kingTile, attackTile);
    const waypoints = simplifyPath(path.length > 0 ? path : [kingTile, attackTile]);

    // Walk to attack position
    this.moveKingAlongPath(waypoints, 60, () => {
      // Play attack animation
      king.play(attackAnim);
      king.once('animationcomplete', () => {
        // Trigger player death animation
        this.playPlayerDeath(eliminatedPlayerId);

        // Return king to podium position
        const returnTile: TileCoord = { col: this.kingPodium.col, row: this.kingPodium.row - 1 };
        const returnPath = findPath(this.roomLayout.walkable, attackTile, returnTile);
        const returnWaypoints = simplifyPath(returnPath.length > 0 ? returnPath : [attackTile, returnTile]);

        this.time.delayedCall(400, () => {
          this.moveKingAlongPath(returnWaypoints, 60, () => {
            king.play('king-idle_south');
            this.kingAtPodium = true;
            onComplete();
          });
        });
      });
    });
  }

  /** King celebrates: jump animation with Y bounce, 3 times, then callback */
  private playKingVictoryJumps(onComplete: () => void): void {
    const king = this.kingSprite;
    if (!king) { this.time.delayedCall(2500, onComplete); return; }

    // Kill any in-flight tweens (attack return, pacing) to avoid position conflicts
    this.tweens.killTweensOf(king);

    // Snap king to correct position in front of the podium
    const yOff = GameScene.KING_Y_OFFSET;
    const frontPx = tileToScreen(this.kingPodium.col, this.kingPodium.row - 1);
    king.x = frontPx.x;
    king.y = frontPx.y + yOff;

    const baseY = king.y;
    let jumpsLeft = 3;

    const doJump = () => {
      if (jumpsLeft <= 0 || !king.active) {
        king.play('king-idle_south');
        this.time.delayedCall(500, onComplete);
        return;
      }
      jumpsLeft--;

      king.play('king-jump_south');
      // Bounce up then down
      this.tweens.add({
        targets: king,
        y: baseY - 8,
        duration: 200,
        ease: 'Sine.easeOut',
        yoyo: true,
        onComplete: () => {
          king.y = baseY;
          this.time.delayedCall(200, doJump);
        },
      });
    };

    this.time.delayedCall(800, doJump);
  }

  /** Convert pixel position to nearest tile coordinate */
  private pixelToTile(px: number, py: number): TileCoord {
    return {
      col: Math.round((px - TILE_SIZE / 2) / TILE_SIZE),
      row: Math.round((py - TILE_SIZE / 2) / TILE_SIZE),
    };
  }

  /** Play the death animation sequence on a player sprite */
  private playPlayerDeath(playerId: string): void {
    const vis = this.playerVisuals.get(playerId);
    if (!vis) return;

    const player = this.allPlayers.find(p => p.id === playerId);
    const charType = player?.characterType || 'wizard';

    vis.sprite.play(`${charType}-dying`);
    vis.sprite.once('animationcomplete', () => {
      vis.sprite.play('player-dead');
      vis.sprite.setTexture('player-dead');
      vis.sprite.setAlpha(0.5);
    });
  }

  // ========================================
  // HUD (layout-driven)
  // ========================================

  private createHUD(): void {
    const L = this.hud;
    const panel = this.add.graphics();

    // HUD panel background
    panel.fillStyle(0x1a1412, 0.95);
    panel.fillRect(L.panelX, L.panelY, L.panelW, L.panelH);
    panel.lineStyle(1, 0x4a3728);
    panel.lineBetween(L.dividerStart.x, L.dividerStart.y, L.dividerEnd.x, L.dividerEnd.y);
    panel.setDepth(9);

    // Round text
    this.roundText = this.add.bitmapText(L.roundText.x, L.roundText.y, 'pixel', `ROUND ${this.roundNumber}`);
    this.roundText.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855).setDepth(10);

    // Timer
    this.timerText = this.add.bitmapText(L.timerText.x, L.timerText.y, 'oldtome', BETTING_DURATION.toString());
    this.timerText.setOrigin(0.5, 0).setScale(1).setTint(0xf5e6d3).setDepth(10);

    // Progress bar
    this.createProgressBar();

    // Phase text
    this.phaseText = this.add.bitmapText(L.phaseText.x, L.phaseText.y, 'pixel', 'BETTING PHASE');
    this.phaseText.setOrigin(0.5, 0).setScale(1).setTint(0xa08060).setDepth(10);

    // Divider 1
    panel.lineBetween(L.divider1.x1, L.divider1.y1, L.divider1.x2, L.divider1.y2);

    // YOUR COINS
    const coinsLabel = this.add.bitmapText(L.coinsLabel.x, L.coinsLabel.y, 'pixel', 'YOUR COINS');
    coinsLabel.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a).setDepth(10);

    this.moneyText = this.add.bitmapText(L.coinsValue.x, L.coinsValue.y, 'oldtome', this.myMoney.toString());
    this.moneyText.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855).setDepth(10);

    // YOUR BET
    const betLabel = this.add.bitmapText(L.betLabel.x, L.betLabel.y, 'pixel', 'YOUR BET');
    betLabel.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a).setDepth(10);

    this.betText = this.add.bitmapText(L.betValue.x, L.betValue.y, 'oldtome', this.myBet.toString());
    this.betText.setOrigin(0.5, 0).setScale(1).setTint(0xf5e6d3).setDepth(10);

    // Divider 2
    panel.lineBetween(L.divider2.x1, L.divider2.y1, L.divider2.x2, L.divider2.y2);

    // Bet buttons (simple graphics)
    const amounts: [1, 5, 10] = [1, 5, 10];
    const btnPositions = [L.betBtn1, L.betBtn2, L.betBtn3];
    amounts.forEach((amount, i) => {
      this.betButtons.push(this.createSimpleButton(
        btnPositions[i].x, btnPositions[i].y,
        btnPositions[i].w, btnPositions[i].h,
        `+${amount}`, amount,
      ));
    });

    // Sound toggle
    const isMuted = SoundManager.muted;
    const soundBtn = this.add.bitmapText(L.soundToggle.x, L.soundToggle.y, 'pixel', isMuted ? 'M' : 'S');
    soundBtn.setOrigin(1, 0).setTint(0xd4a855).setAlpha(0.7).setScale(1).setDepth(10);
    soundBtn.setInteractive({ useHandCursor: true });
    soundBtn.on('pointerover', () => soundBtn.setAlpha(1));
    soundBtn.on('pointerout', () => soundBtn.setAlpha(0.7));
    soundBtn.on('pointerdown', () => {
      const nowMuted = SoundManager.toggleMute();
      soundBtn.setText(nowMuted ? 'M' : 'S');
      if (!nowMuted) SoundManager.click();
    });

    // Spectator banner
    this.spectatorText = this.add.bitmapText(L.spectator.x, L.spectator.y, 'pixel', 'SPECTATING');
    this.spectatorText.setOrigin(0.5, 0).setScale(1).setTint(0xff6b6b).setDepth(10);
    this.spectatorText.setVisible(false);
  }

  // ========================================
  // Simple Bet Buttons (graphics-based)
  // ========================================

  private createSimpleButton(
    cx: number, cy: number, w: number, h: number,
    text: string, amount: 1 | 5 | 10,
  ): SimpleBetButton {
    const container = this.add.container(cx, cy);
    container.setDepth(10);

    const bg = this.add.graphics();
    this.drawButtonBg(bg, w, h, false);
    container.add(bg);

    const label = this.add.bitmapText(0, 0, 'pixel', text);
    label.setOrigin(0.5).setScale(1).setTint(0xf5e6d3);
    container.add(label);

    container.setSize(w, h);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      this.drawButtonBg(bg, w, h, false, true);
    });
    container.on('pointerout', () => {
      this.drawButtonBg(bg, w, h, false);
    });
    container.on('pointerdown', () => {
      this.drawButtonBg(bg, w, h, true);
      label.setY(1);
    });
    container.on('pointerup', () => {
      this.drawButtonBg(bg, w, h, false);
      label.setY(0);
      this.handleBet(amount);
    });

    return { container, bg, label, amount, disabled: false };
  }

  private drawButtonBg(g: Phaser.GameObjects.Graphics, w: number, h: number, pressed: boolean, hover?: boolean): void {
    g.clear();
    const fill = pressed ? 0x2a1e14 : (hover ? 0x5a4a3a : 0x3d2a1f);
    g.fillStyle(fill, 1);
    g.fillRect(-w / 2, -h / 2, w, h);
    g.lineStyle(1, 0xd4a855);
    g.strokeRect(-w / 2, -h / 2, w, h);
  }

  private setSimpleButtonDisabled(btn: SimpleBetButton, disabled: boolean): void {
    btn.disabled = disabled;
    if (disabled) {
      btn.label.setTint(0x555555);
      btn.container.disableInteractive();
      btn.bg.clear();
      btn.bg.fillStyle(0x2a1e14, 1);
      btn.bg.fillRect(-btn.container.width / 2, -btn.container.height / 2, btn.container.width, btn.container.height);
      btn.bg.lineStyle(1, 0x333333);
      btn.bg.strokeRect(-btn.container.width / 2, -btn.container.height / 2, btn.container.width, btn.container.height);
    } else {
      btn.label.setTint(0xf5e6d3);
      btn.container.setInteractive({ useHandCursor: true });
      this.drawButtonBg(btn.bg, btn.container.width, btn.container.height, false);
    }
  }

  // ========================================
  // Progress Bar
  // ========================================

  private createProgressBar(): void {
    const L = this.hud;
    const barW = L.progressBar.w;
    const barH = L.progressBar.h;
    const x = L.progressBar.x;
    const y = L.progressBar.y;

    this.progressBarBg = this.add.graphics();
    this.progressBarBg.fillStyle(0x2d1f1a, 1);
    this.progressBarBg.fillRect(x - barW / 2, y, barW, barH);
    this.progressBarBg.lineStyle(1, 0x4a3728);
    this.progressBarBg.strokeRect(x - barW / 2, y, barW, barH);
    this.progressBarBg.setDepth(10);

    this.progressBarFill = this.add.graphics();
    this.progressBarFill.setDepth(10);
    this.updateProgressBar(1);
  }

  private updateProgressBar(progress: number): void {
    if (!this.progressBarFill) return;
    const L = this.hud;
    const x = L.progressBar.x;
    const y = L.progressBar.y;
    const barW = L.progressBar.w;
    const barH = L.progressBar.h;

    this.progressBarFill.clear();
    if (progress <= 0) return;

    const fillW = (barW - 2) * Math.min(1, progress);
    let color = 0xd4a855;
    if (progress < 0.33) color = 0xff4444;
    else if (progress < 0.5) color = 0xffaa44;

    this.progressBarFill.fillStyle(color, 1);
    this.progressBarFill.fillRect(x - barW / 2 + 1, y + 1, fillW, barH - 2);
  }

  // ========================================
  // Player Sprites
  // ========================================

  private assignPlayerSlots(): void {
    if (this.playerSlots.size > 0) return;

    const others = this.allPlayers.filter(p => p.id !== this.playerId);
    const meIndex = Math.min(1, PLAYER_TILE_POSITIONS.length - 1);
    this.playerSlots.set(this.playerId, meIndex);

    let slot = 0;
    for (const other of others) {
      if (slot === meIndex) slot++;
      if (slot >= PLAYER_TILE_POSITIONS.length) break;
      this.playerSlots.set(other.id, slot);
      slot++;
    }
  }

  private createPlayerSprites(): void {
    this.playerVisuals.clear();
    this.assignPlayerSlots();

    for (const player of this.allPlayers) {
      const slotIndex = this.playerSlots.get(player.id);
      if (slotIndex === undefined || slotIndex >= PLAYER_TILE_POSITIONS.length) continue;
      this.createPlayerVisual(player, slotIndex);
    }
  }

  private createPlayerVisual(player: PlayerPublic, slotIndex: number): void {
    const pos = PLAYER_TILE_POSITIONS[slotIndex];
    const { x, y } = tileToScreen(pos.col, pos.row);
    const isMe = player.id === this.playerId;
    const charType = player.characterType || 'wizard';

    const sprite = this.add.sprite(x, y, `player-${charType}`);
    sprite.setTint(0xf5e6d3);
    sprite.setDepth(5);

    if (player.isAlive) {
      sprite.play(`${charType}-idle`);
    } else {
      sprite.play('player-dead');
      sprite.setTexture('player-dead');
      sprite.setAlpha(0.5);
    }

    const coinThrowX = x;
    const coinThrowY = y - TILE_SIZE;

    const nameStagger = (slotIndex % 2 === 1) ? 6 : 0;
    const nameLabelY = y + TILE_SIZE * 0.7 + nameStagger;

    const nameLabel = this.add.bitmapText(x, nameLabelY, 'pixel', player.name.toUpperCase());
    nameLabel.setOrigin(0.5, 0).setScale(1).setTint(isMe ? 0xd4a855 : 0xa08060).setDepth(5);

    if (!player.isAlive) {
      nameLabel.setAlpha(0.4);
    }

    const betBubble = this.add.container(x, y - TILE_SIZE);
    betBubble.setVisible(false);
    betBubble.setDepth(8);

    if (isMe) {
      const glow = this.add.graphics();
      glow.fillStyle(0xd4a855, 0.15);
      glow.fillCircle(x, y, TILE_SIZE * 0.7);
      glow.setDepth(4);
    }

    if (!player.isConnected) {
      sprite.setAlpha(0.4);
      nameLabel.setAlpha(0.4);
    }

    this.playerVisuals.set(player.id, { sprite, coinThrowX, coinThrowY, nameLabel, betBubble });
  }

  private refreshPlayerSprites(): void {
    for (const player of this.allPlayers) {
      const vis = this.playerVisuals.get(player.id);
      if (!vis) continue;

      if (player.isAlive) {
        const charType = player.characterType || 'wizard';
        vis.sprite.play(`${charType}-idle`);
        vis.sprite.setAlpha(1);
        vis.nameLabel.setAlpha(1);
      }

      vis.betBubble.setVisible(false);
      vis.betBubble.setAlpha(1);
      vis.betBubble.setScale(1);
    }
  }

  // ========================================
  // Bet Indicator (coin throw animation)
  // ========================================

  private showBetIndicator(playerId: string): void {
    const vis = this.playerVisuals.get(playerId);
    if (!vis) return;

    const player = this.allPlayers.find(p => p.id === playerId);
    if (!player || !player.isAlive) return;

    const charType = player.characterType || 'wizard';
    vis.sprite.stop();
    vis.sprite.play(`${charType}-betting`);

    if (vis.idleTimer) {
      vis.idleTimer.destroy();
      vis.idleTimer = undefined;
    }

    const coin = this.add.sprite(vis.coinThrowX, vis.coinThrowY, 'coin-throw');
    coin.setTint(0xd4a855);
    coin.setDepth(6);
    coin.play('coin-throw');
    coin.once('animationcomplete', () => { coin.destroy(); });

    vis.idleTimer = this.time.delayedCall(1000, () => {
      if (vis.sprite.active && player.isAlive) {
        vis.sprite.play(`${charType}-idle`);
      }
      vis.idleTimer = undefined;
    });
  }

  // ========================================
  // Socket Callbacks
  // ========================================

  private setupSocketCallbacks(): void {
    socketManager.setCallbacks({
      onRoundStarted: (roundNumber, phaseEndsAt, yourMoney, yourBet, alivePlayers) => {
        SoundManager.roundStart();
        this.phase = 'betting';
        this.roundNumber = roundNumber;
        this.myMoney = yourMoney;
        this.myBet = yourBet;
        this.alivePlayers = alivePlayers;
        this.phaseEndsAt = phaseEndsAt;
        this.amIAlive = alivePlayers.some(p => p.id === this.playerId);

        if (this.allPlayers.length === 0) {
          this.allPlayers = [...alivePlayers];
          this.createPlayerSprites();
        }

        for (const ap of this.allPlayers) {
          ap.isAlive = alivePlayers.some(p => p.id === ap.id);
        }

        if (this.roundText) {
          this.roundText.setText(`ROUND ${roundNumber}`);
          this.phaseText.setText('BETTING PHASE');
          this.messageText.setText('');
          this.updateMoneyDisplay();
          this.refreshPlayerSprites();
          this.setBetButtonsEnabled(this.amIAlive);
          this.startTimer(phaseEndsAt);
          this.startKingPacing();
        }
      },

      onBetActivity: (playerId) => {
        this.showBetIndicator(playerId);
        SoundManager.coinAdd();
      },

      onBetUpdated: (currentBet, remainingMoney) => {
        this.myBet = currentBet;
        this.myMoney = remainingMoney;
        this.updateMoneyDisplay();
        SoundManager.coinAdd();

        if (this.betText) {
          this.tweens.add({
            targets: this.betText,
            scale: 1.3,
            duration: 100,
            yoyo: true,
            onComplete: () => { if (this.betText) this.betText.setScale(1); },
          });
        }
      },

      onRoundReveal: (bets, phaseEndsAt) => {
        this.phase = 'reveal';
        SoundManager.reveal();
        this.stopKingPacing();
        if (this.kingSprite) this.kingSprite.play('king-idle_south');
        if (this.phaseText) this.phaseText.setText('REVEALING BETS');
        this.setBetButtonsEnabled(false);
        this.stopTimer();
        this.updateProgressBar(0);
        this.showReveal(bets);
      },

      onPlayerEliminated: (result) => {
        this.phase = 'elimination';
        SoundManager.elimination();
        if (this.phaseText) this.phaseText.setText('ELIMINATION');
        this.showElimination(result);

        this.alivePlayers = this.alivePlayers.filter(p => p.id !== result.eliminatedPlayerId);
        const eliminatedPlayer = this.allPlayers.find(p => p.id === result.eliminatedPlayerId);
        if (eliminatedPlayer) eliminatedPlayer.isAlive = false;

        if (result.eliminatedPlayerId === this.playerId) {
          this.amIAlive = false;
          this.showMessage('YOU HAVE BEEN DISMISSED!', 0xff6b6b);
          this.spectatorText.setVisible(true);
        }
      },

      onGameOver: (result) => {
        this.phase = 'gameOver';
        this.registry.set('gameResult', result);
        this.stopKingPacing();

        const isWinner = result.winnerId === this.playerId;
        if (isWinner) {
          SoundManager.victory();
          this.showMessage('VICTORY!', 0xd4a855);
        } else {
          SoundManager.defeat();
          this.showMessage(`${result.winnerName.toUpperCase()} WINS!`, 0xa08060);
        }

        // King celebrates with jump animation before transitioning
        this.playKingVictoryJumps(() => {
          this.scene.start('ResultsScene');
        });
      },

      onPlayerLeft: (playerId, newHostId) => {
        const player = this.alivePlayers.find(p => p.id === playerId);
        if (player) {
          player.isConnected = false;
          const vis = this.playerVisuals.get(playerId);
          if (vis) {
            vis.sprite.setAlpha(0.4);
            vis.nameLabel.setAlpha(0.4);
          }
          this.showMessage(`${player.name.toUpperCase()} DISCONNECTED`, 0xffaa44);
        }
      },

      onError: (message) => {
        console.error('[Game] Error:', message);
      },
    });
  }

  // ========================================
  // Game Logic
  // ========================================

  private handleBet(amount: 1 | 5 | 10): void {
    if (this.phase !== 'betting' || !this.amIAlive) return;
    if (this.myMoney < amount) return;
    socketManager.addBet(amount);
  }

  private updateMoneyDisplay(): void {
    if (!this.moneyText || !this.betText) return;
    this.moneyText.setText(this.myMoney.toString());
    this.betText.setText(this.myBet.toString());

    if (this.betButtons.length >= 3) {
      this.setSimpleButtonDisabled(this.betButtons[0], this.myMoney < 1 || this.phase !== 'betting');
      this.setSimpleButtonDisabled(this.betButtons[1], this.myMoney < 5 || this.phase !== 'betting');
      this.setSimpleButtonDisabled(this.betButtons[2], this.myMoney < 10 || this.phase !== 'betting');
    }

    if (this.myMoney === 0 && this.amIAlive && this.phase === 'betting') {
      this.showMessage('NO COINS LEFT!', 0xffaa44);
    }
  }

  private setBetButtonsEnabled(enabled: boolean): void {
    const amounts = [1, 5, 10];
    this.betButtons.forEach((btn, index) => {
      this.setSimpleButtonDisabled(btn, !enabled || this.myMoney < amounts[index]);
    });
  }

  private initializeDefaultTimer(): void {
    this.phaseEndsAt = Date.now() + BETTING_DURATION * 1000;
    this.startTimer(this.phaseEndsAt);
  }

  private startTimer(phaseEndsAt: number): void {
    this.stopTimer();
    this.phaseEndsAt = phaseEndsAt;
    const totalDuration = BETTING_DURATION * 1000;

    const updateTimer = () => {
      const remaining = Math.max(0, this.phaseEndsAt - Date.now());
      const seconds = Math.ceil(remaining / 1000);

      if (this.timerText) {
        this.timerText.setText(seconds.toString());
        if (seconds <= 3) {
          this.timerText.setTint(0xff4444);
          if (seconds > 0 && remaining % 1000 < 50) {
            SoundManager.timerWarning();
          }
        } else if (seconds <= 5) {
          this.timerText.setTint(0xffaa44);
        } else {
          this.timerText.setTint(0xf5e6d3);
        }
      }

      this.updateProgressBar(remaining / totalDuration);
    };

    updateTimer();
    this.timerEvent = this.time.addEvent({ delay: 50, callback: updateTimer, loop: true });
  }

  private stopTimer(): void {
    if (this.timerEvent) {
      this.timerEvent.destroy();
      this.timerEvent = null;
    }
  }

  // ========================================
  // Reveal & Elimination
  // ========================================

  private showReveal(bets: BetResult[]): void {
    const sortedBets = [...bets].sort((a, b) => a.amount - b.amount);
    const lowestAmount = sortedBets[0]?.amount;

    bets.forEach((bet, index) => {
      const vis = this.playerVisuals.get(bet.playerId);
      if (!vis) return;

      vis.betBubble.removeAll(true);
      vis.betBubble.setVisible(true);

      const isLowest = bet.amount === lowestAmount;

      const bg = this.add.graphics();
      bg.fillStyle(isLowest ? 0x8b0000 : 0x2d1f1a, 0.9);
      bg.fillCircle(0, 0, 7);
      bg.lineStyle(1, isLowest ? 0xff4444 : 0xd4a855);
      bg.strokeCircle(0, 0, 7);
      vis.betBubble.add(bg);

      const label = this.add.bitmapText(0, 0, 'pixel', `${bet.amount}`);
      label.setOrigin(0.5).setScale(1).setTint(isLowest ? 0xff6b6b : 0xd4a855);
      vis.betBubble.add(label);

      vis.betBubble.setScale(0);
      vis.betBubble.setAlpha(0);
      this.tweens.add({
        targets: vis.betBubble,
        scale: 1,
        alpha: 1,
        duration: 300,
        delay: index * 150,
        ease: 'Back.easeOut',
      });
    });

    const uniqueBets = new Set(bets.map(b => b.amount));
    if (uniqueBets.size === 1 && bets.length > 1) {
      this.showMessage('ALL BETS ARE THE SAME!', 0xffaa44);
    } else {
      this.showMessage('ALL BETS REVEALED!', 0xd4a855);
    }
  }

  private showElimination(result: EliminationResult): void {
    let message = `${result.eliminatedPlayerName.toUpperCase()} IS DISMISSED!`;
    if (result.wasTieBreaker) {
      message = `TIE! ${result.eliminatedPlayerName.toUpperCase()} DISMISSED!`;
    }
    this.showMessage(message, 0xff6b6b);

    const vis = this.playerVisuals.get(result.eliminatedPlayerId);
    if (vis && vis.betBubble.visible) {
      this.tweens.add({
        targets: vis.betBubble,
        alpha: 0,
        scale: 0,
        duration: 500,
      });
    }

    // King attacks the player, then their death animation plays
    this.playKingAttack(result.eliminatedPlayerId, () => {
      // King is back at podium — ready for next round
    });
  }

  private showMessage(text: string, color: number = 0xd4a855): void {
    if (!this.messageText) return;
    this.messageText.setText(text);
    this.messageText.setTint(color);

    this.messageText.setAlpha(0);
    this.messageText.setScale(0.8);
    this.tweens.add({
      targets: this.messageText,
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  shutdown(): void {
    this.stopTimer();
    this.stopKingPacing();
    socketManager.clearCallbacks();
  }
}
